import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Skeleton,
} from '@billda/design-system';
import {
  CloseIcon,
  HourglassEmptyIcon,
  PlayCircleOutlineIcon,
} from '@billda/design-system';
import { PermissionGate } from '../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { useBookingImages } from '../hooks/useBookingImages';
import type { BookingImageResponse } from '../types/bookingImage';
import {
  getCachedUrl,
  cacheImage,
} from '../../variations/services/variationImageCache';

interface BookingImageGalleryProps {
  allocationId: string;
  minimal?: boolean;
}

export function BookingImageGallery({
  allocationId,
  minimal,
}: BookingImageGalleryProps) {
  return (
    <PermissionGate permission={Permissions.Labour.BookingImages.Read}>
      <BookingImageGalleryContent
        allocationId={allocationId}
        minimal={minimal}
      />
    </PermissionGate>
  );
}

interface PreviewMedia {
  url: string;
  contentType: string;
}

function isVideo(contentType: string) {
  return contentType.startsWith('video/');
}

function BookingImageGalleryContent({
  allocationId,
  minimal,
}: BookingImageGalleryProps) {
  const { data: images, isLoading, error } = useBookingImages(allocationId);
  const [preview, setPreview] = useState<PreviewMedia | null>(null);
  const [resolvedUrls, setResolvedUrls] = useState<Map<string, string>>(
    new Map()
  );

  const allImages = images ?? [];
  const uploadedImages = allImages.filter(img => img.status === 'Uploaded');
  const pendingCount = allImages.filter(img => img.status === 'Pending').length;

  // Resolve and cache blob URLs for uploaded images
  useEffect(() => {
    if (uploadedImages.length === 0) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const updates = new Map<string, string>();

      for (const img of uploadedImages) {
        if (!img.url) {
          continue;
        }
        const cached = getCachedUrl(img.contentHash);
        if (cached) {
          updates.set(img.id, cached);
        } else {
          try {
            const url = await cacheImage(img.contentHash, img.url);
            if (cancelled) {
              return;
            }
            updates.set(img.id, url);
          } catch {
            // Fall back to SAS URL if caching fails
            updates.set(img.id, img.url);
          }
        }
      }

      if (!cancelled) {
        setResolvedUrls(prev => {
          const next = new Map(prev);
          for (const [k, v] of updates) {
            next.set(k, v);
          }
          return next;
        });
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // Re-run when the image list changes (new uploads or refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImages.map(img => img.contentHash).join(',')]);

  if (isLoading) {
    const grid = (
      <div
        className="grid grid-cols-2 gap-1.5"
        data-testid="booking-images-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            className="aspect-square rounded-lg"
          />
        ))}
      </div>
    );
    if (minimal) {
      return grid;
    }
    return (
      <Card>
        <CardContent className="pt-4">
          <h6 className="type-h6 mb-4">Photos</h6>
          {grid}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="booking-images-error">
        Failed to load booking images
      </Alert>
    );
  }

  if (allImages.length === 0) {
    return minimal ? (
      <p className="type-body-md text-content-secondary">No photos added</p>
    ) : null;
  }

  const handlePreview = (img: BookingImageResponse) => {
    const url = resolvedUrls.get(img.id) ?? img.url;
    if (url) {
      setPreview({ url, contentType: img.contentType });
    }
  };

  const grid = (
    <div
      data-testid="booking-images-gallery"
      className="grid grid-cols-2 gap-1.5"
    >
      {uploadedImages.map(img => {
        const displayUrl = resolvedUrls.get(img.id) ?? img.url;
        const video = isVideo(img.contentType);

        return (
          <button
            key={img.id}
            type="button"
            aria-label={`Preview ${img.originalFileName}`}
            data-testid={`booking-image-thumbnail-${img.id}`}
            onClick={() => handlePreview(img)}
            disabled={!displayUrl}
            className="group relative overflow-hidden rounded-lg border-none bg-transparent p-0 disabled:cursor-default enabled:cursor-pointer"
          >
            {!displayUrl ? (
              <Skeleton
                variant="rectangular"
                className="aspect-square w-full"
              />
            ) : video ? (
              <>
                <video
                  src={displayUrl}
                  muted
                  className="block aspect-square w-full object-cover"
                />
                <PlayCircleOutlineIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1/2 top-1/2 size-9 -translate-x-1/2 -translate-y-1/2 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]"
                />
              </>
            ) : (
              <img
                src={displayUrl}
                alt={img.originalFileName}
                className="block aspect-square w-full object-cover"
              />
            )}
          </button>
        );
      })}

      {/* Pending image placeholders */}
      {Array.from({ length: pendingCount }).map((_, i) => (
        <div
          key={`pending-${i}`}
          data-testid="booking-image-pending"
          className="flex aspect-square flex-col items-center justify-center gap-0.5 rounded-lg bg-surface-raised"
        >
          <HourglassEmptyIcon
            aria-hidden="true"
            className="size-5 text-content-disabled"
          />
          <span className="type-caption px-1 text-center leading-[1.2] text-content-disabled">
            Awaiting upload
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {minimal ? (
        grid
      ) : (
        <Card>
          <CardContent className="pt-4">
            <div className="mb-4 flex items-center justify-between">
              <h6 className="type-h6">Photos</h6>
              <span className="type-body-md text-content-secondary">
                {allImages.length}
              </span>
            </div>
            {grid}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!preview}
        onOpenChange={open => {
          if (!open) {
            setPreview(null);
          }
        }}
      >
        {preview && (
          <DialogContent
            aria-label="Media preview"
            className="m-4 max-h-[90vh] w-auto max-w-[90vw] border-border-default bg-black p-0 [&>button:last-child]:hidden"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreview(null)}
              aria-label="Close preview"
              data-testid="booking-image-preview-close"
              className="absolute right-2 top-2 z-[1] text-white hover:bg-surface-overlay"
            >
              <CloseIcon aria-hidden="true" className="size-5" />
            </Button>
            {isVideo(preview.contentType) ? (
              <video
                src={preview.url}
                controls
                autoPlay
                className="block max-h-[85vh] max-w-full"
              />
            ) : (
              <img
                src={preview.url}
                alt="Preview"
                className="block max-h-[85vh] max-w-full object-contain"
              />
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
